import React, { FC, useCallback } from 'react';
import { useFormikContext } from 'formik';

import { FormInput } from 'Shared/components';
import { AddressType } from 'Shared/utils';
import { useAppContext } from 'QuoteApp/services/providers';
import { Suggestion } from 'Shared/hooks/useAddressLookup/useAddressLookup';

import { FormErrorBanner } from '../FormErrorBanner';
import {
  GaragingAddressFormType,
  GaragingAddressProps,
  GaragingAddressType,
} from './types';

export const GaragingAddress: FC<GaragingAddressProps> = ({
  vehicleId,
  primaryAddressState,
  hasError = false,
  disabled = false,
  readOnly = false,
}) => {
  const { details } = useAppContext();
  const { values, touched, errors, handleBlur, setValues, setFieldValue } =
    useFormikContext<GaragingAddressFormType>();

  const fieldNamePrefix = `vehicles[${vehicleId}].garagingAddress`;

  const buildGaragingAddressFormValues = (vehicleId: string): AddressType => ({
    address1: values.vehicles[vehicleId].garagingAddress.addressLine1 ?? '',
    address2: values.vehicles[vehicleId].garagingAddress.addressLine2 ?? '',
    city: values.vehicles[vehicleId].garagingAddress.city ?? '',
    state: primaryAddressState,
    zip: values.vehicles[vehicleId].garagingAddress.postalCode ?? '',
  });

  const isMissingAddressLine2 =
    details.vehicles[vehicleId].garagingAddressValidation?.isMissingLineTwo;

  const buildGaragingAddressErrorMessages = (vehicleId: string) => {
    return {
      address1: touched.vehicles?.[vehicleId]?.garagingAddress?.addressLine1
        ? errors.vehicles?.[vehicleId]?.garagingAddress?.addressLine1
        : undefined,
      address2:
        errors.vehicles?.[vehicleId]?.garagingAddress?.addressLine2 ||
        isMissingAddressLine2
          ? 'Line 2 is required'
          : undefined,
      city: touched.vehicles?.[vehicleId]?.garagingAddress?.city
        ? errors.vehicles?.[vehicleId]?.garagingAddress?.city
        : undefined,
      state: touched.vehicles?.[vehicleId]?.garagingAddress?.state
        ? errors.vehicles?.[vehicleId]?.garagingAddress?.state
        : undefined,
      zip: touched.vehicles?.[vehicleId]?.garagingAddress?.postalCode
        ? errors.vehicles?.[vehicleId]?.garagingAddress?.postalCode
        : undefined,
    };
  };

  const setAddressFieldValue = useCallback(
    <K extends keyof GaragingAddressType>(
      key: K
    ): ((value: GaragingAddressType[K]) => void) => {
      return (value: GaragingAddressType[K]) => {
        setFieldValue(`${fieldNamePrefix}.${key}`, value);
      };
    },
    [fieldNamePrefix, setFieldValue]
  );

  const overrideFormFieldValues = (selectedOption: Suggestion) => {
    const { city, state, streetLine } = selectedOption;

    setValues((prevValues) => ({
      ...prevValues,
      vehicles: {
        ...prevValues.vehicles,
        [vehicleId]: {
          ...prevValues.vehicles[vehicleId],
          garagingAddress: {
            ...prevValues.vehicles[vehicleId].garagingAddress,
            addressLine1: streetLine,
            city: city,
            state: state,
            postalCode: '',
          },
        },
      },
    }));
  };

  const renderError = () => {
    return (
      <FormErrorBanner message="We’re sorry, we were unable to validate this address. Please update your garaging address by hitting the Edit button before continuing." />
    );
  };

  return (
    <>
      {hasError && renderError()}
      <FormInput
        variant="addressAutocomplete"
        allowStateChange={false}
        prefix={fieldNamePrefix}
        values={buildGaragingAddressFormValues(vehicleId)}
        onAddressSelect={overrideFormFieldValues}
        onAddressLine1Change={setAddressFieldValue('addressLine1')}
        onAddressLine2Change={setAddressFieldValue('addressLine2')}
        onCityChange={setAddressFieldValue('city')}
        onStateChange={() => {}}
        onZipChange={setAddressFieldValue('postalCode')}
        errorMessages={buildGaragingAddressErrorMessages(vehicleId)}
        onBlur={handleBlur}
        disabled={disabled}
        readOnly={readOnly}
      />
    </>
  );
};
